import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Control, Controller, useFormState } from 'react-hook-form';
import { IServiceShareInputCreateRequestModel } from '../../../models/serviceShareInput/ServiceShareInputCreateRequestModel';
import styles from './AddServiceShareInput.module.scss';
import { Dropdown } from 'primereact/dropdown';
import { IServiceShareInputFiltersOptions } from '../../../models/serviceShareInput/ServiceShareFiltersModel';

export interface IAddServiceShareInputProps {
	control: Control<IServiceShareInputCreateRequestModel>;
	dropdownsOptions: IServiceShareInputFiltersOptions;
	isInputDialogVisible: boolean;
	onDialogVisibleChange: (isVisible: boolean) => void;
	isLoading: boolean;
	handleSubmitRequest: () => Promise<void>;
}

export const AddServiceShareInput = ({
	control,
	dropdownsOptions,
	isInputDialogVisible,
	onDialogVisibleChange,
	isLoading,
	handleSubmitRequest
}: IAddServiceShareInputProps) => {
	const { isSubmitted, isValid } = useFormState({control});

	const isSubmitDisabled = isSubmitted && !isValid;

	const addDialogFooter: JSX.Element = (
		<>
			<button 
				className='btn primary' 
				onClick={handleSubmitRequest}
				disabled={isSubmitDisabled}
			>
				Add
			</button>
			<button
				className='btn secondary'
				onClick={() => onDialogVisibleChange(false)}
			>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles['dialog__footer']}>
			<p>Request is processing...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				style={{ marginLeft: 'auto' }}
				className={`${styles.addButton} btn primary`}
				onClick={() => onDialogVisibleChange(true)}
			>
				Add Service Share
			</button>

			<Dialog
				className={styles['dialog']}
				header='Add Service Share'
				onHide={() => onDialogVisibleChange(false)}
				visible={isInputDialogVisible}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles['dialog__content']}>
					<Controller
						name='distributorOrEntity'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='fridgeTechnicians'>
									Distributor/Entity
								</label>
								<InputText
									id={field.name}
									ref={ref}
									{...field}
									placeholder='Distributor/Entity'
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='numberOfEmployees'
						control={control}
						render={({ field, fieldState }) => (
							<>
								<label htmlFor='numberOfEmployees'>
									Number of employees
								</label>
								<InputNumber
									id={field.name}
									ref={field.ref}
									value={field.value}
									onBlur={field.onBlur}
									onValueChange={(e) => field.onChange(e)}
									useGrouping={false}
									minFractionDigits={0} 
									maxFractionDigits={2}
									placeholder='Number of employees'
									mode='decimal'
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										{fieldState.error.message}
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='numberOfTechnicians'
						control={control}
						render={({ field, fieldState }) => (
							<>
								<label htmlFor='numberOfTechnicians'>
									Number of technicians
								</label>
								<InputNumber
									id={field.name}
									ref={field.ref}
									value={field.value}
									onBlur={field.onBlur}
									onValueChange={(e) => field.onChange(e)}
									useGrouping={false}
									minFractionDigits={0} 
									maxFractionDigits={2}
									placeholder='Number of technicians'
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										{fieldState.error.message}
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='fridgeTechnicians'
						control={control}
						render={({ field, fieldState }) => (
							<>
								<label htmlFor='fridgeTechnicians'>
									Fridge technicians
								</label>
								<InputNumber
									id={field.name}
									ref={field.ref}
									value={field.value}
									onBlur={field.onBlur}
									onValueChange={(e) => field.onChange(e)}
									useGrouping={false}
									placeholder='Fridge technicians'
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='channel'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='channel'>Channel</label>
								<Dropdown
									id={field.name}
									ref={ref}
									{...field}
									placeholder='Channel'
									options={dropdownsOptions.channelsOptions}
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='countryId'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='countryId'>Country</label>
								<Dropdown
									id={field.name}
									{...field}
									ref={ref}
									placeholder='Country'
									options={dropdownsOptions.countriesOptions}
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='quarter'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='quarter'>Quarter</label>
								<Dropdown
									id={field.name}
									ref={ref}
									{...field}
									placeholder='Quarter'
									options={dropdownsOptions.quartersOptions}
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='year'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='year'>Year</label>
								<Dropdown
									id={field.name}
									ref={ref}
									{...field}
									placeholder='Year'
									options={dropdownsOptions.yearsOptions}
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>

				<div className={styles['dialog__content']}>
					<Controller
						name='brandId'
						control={control}
						render={({ field: { ref, ...field }, fieldState }) => (
							<>
								<label htmlFor='brandId'>Brand</label>
								<Dropdown
									id={field.name}
									ref={ref}
									{...field}
									placeholder='Brand'
									options={dropdownsOptions.brandsOptions}
									className={
										fieldState.error
											? 'p-invalid block'
											: ''
									}
								/>
								{fieldState.error && (
									<small className='p-error block'>
										Insert values
									</small>
								)}
							</>
						)}
					/>
				</div>
			</Dialog>
		</>
	);
};
